import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Watch, CheckCircle, XCircle, Loader } from 'lucide-react';
import { authAPI } from '../services/api';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Get token from URL query parameters (sanitize: max 200 chars to prevent abuse)
    const tokenParam = searchParams.get('token');
    if (tokenParam && tokenParam.length <= 200) {
      setToken(tokenParam);
      verifyEmail(tokenParam);
    } else if (tokenParam) {
      setStatus('error');
      setMessage('Invalid verification link. Please request a new one.');
    } else {
      setStatus('error');
      setMessage('No verification token provided. Please check your email for the correct link.');
    }
  }, [searchParams]);

  const verifyEmail = async (verificationToken) => {
    try {
      const response = await authAPI.verifyEmail(verificationToken);
      setStatus('success');
      setMessage(response.data.message || 'Email verified successfully!');

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Failed to verify email. The link may have expired.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <Watch className="w-12 h-12 text-amber-400" />
            <h1 className="text-4xl font-bold text-white">Dial-Grid</h1>
          </div>
          <p className="text-slate-300">Watch Dealer Platform</p>
        </div>

        {/* Verification Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl shadow-2xl p-8 border border-white/20">
          {status === 'verifying' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader className="w-12 h-12 text-blue-400 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Verifying Your Email</h2>
              <p className="text-slate-300">
                Please wait while we verify your email address...
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Email Verified!</h2>
              <p className="text-slate-300 mb-6">
                {message}
              </p>
              <p className="text-sm text-slate-400">
                Redirecting to login page in 3 seconds...
              </p>
              <Link
                to="/login"
                className="mt-6 inline-block px-6 py-3 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl font-bold hover:from-amber-500 hover:to-amber-400 transition-all"
              >
                Go to Login
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Verification Failed</h2>
              <p className="text-slate-300 mb-6">
                {message}
              </p>
              <div className="space-y-3">
                <Link
                  to="/login"
                  className="block w-full px-6 py-3 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl font-bold hover:from-amber-500 hover:to-amber-400 transition-all"
                >
                  Go to Login
                </Link>
                <p className="text-sm text-slate-400">
                  Need a new verification link?{' '}
                  <Link to="/login" className="text-amber-400 hover:text-amber-300 font-semibold">
                    Resend from login page
                  </Link>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
